/**
 * EmptyState — Context-aware empty state with three variants.
 * Story 15.4: Context-Aware Empty States (AC-1 through AC-6)
 *
 * Variants:
 * - caught-up: Returning user, queue/filter empty — congratulatory
 * - first-visit: New user, no historical data — onboarding
 * - system-issue: API error or SSE disconnection — error guidance
 *
 * Features:
 * - Consistent layout: illustration, heading, description, actions
 * - Glass-Forward card with accessibility
 * - aria-live="polite" for system-issue variant
 * - Respects prefers-contrast and prefers-reduced-motion
 * - Auto-retry indicator for system-issue
 */

import { type ReactNode } from "react";
import {
  CheckCircle,
  Compass,
  AlertTriangle,
  RefreshCw,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";

// ─── Types ──────────────────────────────────────────────────────

type EmptyStateVariant = "caught-up" | "first-visit" | "system-issue";

interface EmptyStateAction {
  label: string;
  onClick?: () => void;
  href?: string;
  variant?: "primary" | "secondary";
}

interface EmptyStateProps {
  variant: EmptyStateVariant;
  icon?: ReactNode;
  heading: string;
  description: string | ReactNode;
  primaryAction?: EmptyStateAction;
  secondaryActions?: EmptyStateAction[];
  metrics?: {
    label: string;
    value: string | number;
  }[];
  /** Show auto-retry countdown (seconds remaining) */
  retryCountdown?: number;
  /** Whether auto-retry is in progress */
  isRetrying?: boolean;
  className?: string;
}

// ─── Default Icons ──────────────────────────────────────────────

const DEFAULT_ICONS: Record<EmptyStateVariant, ReactNode> = {
  "caught-up": (
    <div className="h-16 w-16 rounded-full bg-[var(--readiness-excellent)]/15 flex items-center justify-center">
      <CheckCircle
        size={32}
        className="text-[var(--readiness-excellent)]"
        aria-hidden="true"
      />
    </div>
  ),
  "first-visit": (
    <div className="h-16 w-16 rounded-full bg-[var(--info)]/15 flex items-center justify-center">
      <Compass size={32} className="text-[var(--info)]" aria-hidden="true" />
    </div>
  ),
  "system-issue": (
    <div className="h-16 w-16 rounded-full bg-[var(--destructive)]/15 flex items-center justify-center">
      <AlertTriangle
        size={32}
        className="text-[var(--destructive)]"
        aria-hidden="true"
      />
    </div>
  ),
};

// ─── Component ──────────────────────────────────────────────────

function EmptyState({
  variant,
  icon,
  heading,
  description,
  primaryAction,
  secondaryActions,
  metrics,
  retryCountdown,
  isRetrying,
  className,
}: EmptyStateProps) {
  const isSystemIssue = variant === "system-issue";

  return (
    <div
      role={isSystemIssue ? "alert" : "status"}
      aria-live={isSystemIssue ? "assertive" : "polite"}
      className={cn(
        "flex flex-col items-center justify-center text-center",
        "px-6 py-12 glass-card rounded-lg",
        "motion-safe:animate-[fade-in_200ms_ease-out]",
        className,
      )}
    >
      {/* Icon */}
      <div className="mb-4">{icon ?? DEFAULT_ICONS[variant]}</div>

      {/* Heading */}
      <h2 className="text-heading-2 text-[var(--text-primary)] mb-2">
        {heading}
      </h2>

      {/* Description */}
      <div className="text-body text-[var(--text-secondary)] mb-6 max-w-md">
        {typeof description === "string" ? <p>{description}</p> : description}
      </div>

      {/* Caught-up metrics */}
      {variant === "caught-up" && metrics && metrics.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-6 w-full max-w-sm">
          {metrics.map((metric) => (
            <div
              key={metric.label}
              className="glass-card rounded-lg p-3 text-center"
            >
              <div className="text-heading-3 text-[var(--text-primary)]">
                {metric.value}
              </div>
              <div className="text-caption text-[var(--text-muted)]">
                {metric.label}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Auto-retry indicator */}
      {isSystemIssue && (retryCountdown !== undefined || isRetrying) && (
        <div className="flex items-center gap-2 mb-4 text-caption text-[var(--text-muted)]">
          {isRetrying ? (
            <>
              <Loader2
                size={12}
                className="motion-safe:animate-spin"
                aria-hidden="true"
              />
              Retrying...
            </>
          ) : retryCountdown !== undefined ? (
            <>
              <RefreshCw size={12} aria-hidden="true" />
              Auto-retry in {retryCountdown}s
            </>
          ) : null}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 flex-wrap justify-center">
        {primaryAction && <ActionButton action={primaryAction} isPrimary />}
        {secondaryActions?.map((action) => (
          <ActionButton key={action.label} action={action} isPrimary={false} />
        ))}
      </div>
    </div>
  );
}

function ActionButton({
  action,
  isPrimary,
}: {
  action: EmptyStateAction;
  isPrimary: boolean;
}) {
  const isActualPrimary = isPrimary || action.variant === "primary";

  const classes = cn(
    "inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium",
    "transition-colors",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]",
    isActualPrimary
      ? "bg-[var(--primary)] text-[var(--primary-foreground)] hover:bg-[var(--primary)]/90"
      : "glass-card hover:bg-[var(--surface-subtle)] text-[var(--text-secondary)]",
  );

  if (action.href) {
    return (
      <a href={action.href} className={classes}>
        {action.label}
      </a>
    );
  }

  return (
    <button type="button" onClick={action.onClick} className={classes}>
      {action.label}
    </button>
  );
}

// ─── Preset Empty States ────────────────────────────────────────

const SURFACE_PRESETS = {
  homeFeed: {
    "caught-up": {
      heading: "Nothing needs your attention",
      description:
        "Your agents are running smoothly. We will notify you when something needs review.",
    },
    "first-visit": {
      heading: "Welcome to Zero Touch Data",
      description:
        "When your agents need attention, items will appear here. Start by connecting a data source.",
    },
    "system-issue": {
      heading: "Feed unavailable",
      description:
        "Unable to load feed items. This may be a temporary connection issue.",
    },
  },
  reviewQueue: {
    "caught-up": {
      heading: "All caught up!",
      description: "No pending reviews. Great work keeping the queue clear.",
    },
    "first-visit": {
      heading: "Welcome to the Review Queue",
      description:
        "When agent decisions need human review, they will appear here. Configure HITL gates in your pipeline to get started.",
    },
    "system-issue": {
      heading: "Reviews unavailable",
      description:
        "Unable to load review items. Check your pipeline status or try again.",
    },
  },
  opsDashboard: {
    "caught-up": {
      heading: "All systems nominal",
      description: "No active alerts or issues requiring attention.",
    },
    "first-visit": {
      heading: "Operations Dashboard",
      description:
        "Pipeline health, agent performance, and system metrics will appear here once pipelines are running.",
    },
    "system-issue": {
      heading: "Dashboard unavailable",
      description: "Unable to load operations data. Check system connectivity.",
    },
  },
  governanceInbox: {
    "caught-up": {
      heading: "Inbox clear",
      description:
        "No governance actions pending. All compliance checks are passing.",
    },
    "first-visit": {
      heading: "Governance Inbox",
      description:
        "Compliance reviews, audit requests, and policy approvals will appear here.",
    },
    "system-issue": {
      heading: "Inbox unavailable",
      description:
        "Unable to load governance items. Contact your administrator if this persists.",
    },
  },
  searchResults: {
    "caught-up": {
      heading: "No results found",
      description: "Try adjusting your search terms or filters.",
    },
    "first-visit": {
      heading: "No results found",
      description: "Try adjusting your search terms or filters.",
    },
    "system-issue": {
      heading: "Search unavailable",
      description: "Unable to perform search. Please try again in a moment.",
    },
  },
  pipelineList: {
    "caught-up": {
      heading: "No pipeline runs",
      description:
        "All pipelines are idle. Start a new run or check scheduled pipelines.",
    },
    "first-visit": {
      heading: "No pipelines yet",
      description:
        "Create your first pipeline to start processing data. Use a template or build a custom pipeline.",
    },
    "system-issue": {
      heading: "Pipelines unavailable",
      description:
        "Unable to load pipeline data. Check your connection and try again.",
    },
  },
} as const;

type SurfaceKey = keyof typeof SURFACE_PRESETS;

export {
  EmptyState,
  SURFACE_PRESETS,
  type EmptyStateProps,
  type EmptyStateVariant,
  type EmptyStateAction,
  type SurfaceKey,
};
